// @ts-nocheck
// Runtime Orchestrator — Fase 14.6
import { obs } from '@/core/observability'
import { canRun, recordFailure, recordSuccess, type SafeModule } from '@/core/safety/operationalSafetyLayer'

export type EngineStatus = 'pending' | 'starting' | 'running' | 'failed' | 'stopped'
export interface EngineRegistration { id: string; module: SafeModule; priority: number; start: () => Promise<void> | void; stop: () => void; healthCheck?: () => boolean; deps: string[] }
export interface EngineState { id: string; status: EngineStatus; startedAt?: number; error?: string }

const _registry  = new Map<string, EngineRegistration>()
const _states    = new Map<string, EngineState>()
const _listeners = new Map<string, Array<(s: EngineState) => void>>()
let   _running   = false

export function registerEngine(reg: EngineRegistration): void { _registry.set(reg.id, reg); _states.set(reg.id, { id: reg.id, status: 'pending' }) }
export function unregisterEngine(id: string): void { _registry.delete(id); _states.delete(id) }

function setState(id: string, update: Partial<EngineState>): void {
  const prev = _states.get(id) ?? { id, status: 'pending' as EngineStatus }
  const next = { ...prev, ...update }; _states.set(id, next)
  const fns = _listeners.get(id) ?? []; fns.forEach((fn) => { try { fn(next) } catch { /* ignore */ } })
}

export function onEngineState(id: string, fn: (s: EngineState) => void): () => void {
  const existing = _listeners.get(id) ?? []; existing.push(fn); _listeners.set(id, existing)
  return () => { const arr = _listeners.get(id) ?? []; const i = arr.indexOf(fn); if (i >= 0) arr.splice(i, 1) }
}
export function getEngineState(id: string): EngineState | null { return _states.get(id) ?? null }
export function getAllEngineStates(): EngineState[] { return [..._states.values()] }

export async function startOrchestrator(): Promise<void> {
  if (_running) { obs.warn('orchestrator', 'Already running'); return }
  _running = true; obs.info('orchestrator', 'Starting')
  const sorted = [..._registry.values()].sort((a, b) => a.priority - b.priority)
  for (const engine of sorted) await startEngine(engine.id)
  obs.info('orchestrator', 'All engines processed', { running: [..._states.values()].filter((s) => s.status === 'running').length, failed: [..._states.values()].filter((s) => s.status === 'failed').length })
}

async function startEngine(id: string): Promise<void> {
  const reg = _registry.get(id); if (!reg) return
  if (!canRun(reg.module)) { obs.info('orchestrator', `Engine "${id}" skipped (safety)`); setState(id, { status: 'stopped' }); return }
  for (const dep of reg.deps) {
    const ds = _states.get(dep)
    if (ds?.status === 'failed' || ds?.status === 'stopped') { obs.warn('orchestrator', `Engine "${id}" skipped: dep "${dep}" is ${ds.status}`); setState(id, { status: 'stopped' }); return }
  }
  setState(id, { status: 'starting' }); const t0 = Date.now()
  try { await reg.start(); setState(id, { status: 'running', startedAt: Date.now() }); recordSuccess(reg.module); obs.info('orchestrator', `Engine "${id}" started in ${Date.now() - t0}ms`) }
  catch (err) { setState(id, { status: 'failed', error: String(err) }); recordFailure(reg.module); obs.error('orchestrator', `Engine "${id}" failed: ${String(err)}`) }
}

export function shutdownOrchestrator(): void {
  if (!_running) return; obs.info('orchestrator', 'Shutting down')
  const sorted = [..._registry.values()].sort((a, b) => b.priority - a.priority)
  for (const engine of sorted) {
    if (_states.get(engine.id)?.status !== 'running') continue
    try { engine.stop(); setState(engine.id, { status: 'stopped' }); obs.info('orchestrator', `Engine "${engine.id}" stopped`) }
    catch (err) { obs.warn('orchestrator', `Engine "${engine.id}" stop threw: ${String(err)}`) }
  }
  _running = false; obs.info('orchestrator', 'Shutdown complete')
}

export interface OrchestratorHealth { timestamp: string; overall: 'healthy' | 'degraded' | 'critical'; running: number; failed: number; stopped: number; failedEngines: string[] }
export function getOrchestratorHealth(): OrchestratorHealth {
  const all = [..._states.values()]
  const running = all.filter((s) => s.status === 'running')
  const failed  = all.filter((s) => s.status === 'failed')
  const stopped = all.filter((s) => s.status === 'stopped')
  const unhealthy: string[] = []
  for (const s of running) { const r = _registry.get(s.id); if (r?.healthCheck && !r.healthCheck()) unhealthy.push(s.id) }
  const totalFailed = failed.length + unhealthy.length
  return { timestamp: new Date().toISOString(), overall: totalFailed === 0 ? 'healthy' : totalFailed <= 2 ? 'degraded' : 'critical', running: running.length, failed: totalFailed, stopped: stopped.length, failedEngines: [...failed.map((s) => s.id), ...unhealthy] }
}

export async function reconnectEngine(id: string): Promise<void> {
  if (!_registry.get(id)) { obs.warn('orchestrator', `Reconnect: "${id}" not registered`); return }
  obs.info('orchestrator', `Reconnecting "${id}"`); await startEngine(id)
}
